/**
 * Builds {@link GenerateGreetingRequest} for the shared AI writing session.
 *
 * Instructions mapping contract (PBI #1152) — same UI for e-card and mailed:
 *
 * | UI source                              | Mode | Request field     | When                          |
 * |----------------------------------------|------|-------------------|-------------------------------|
 * | Session rewrite-instructions textbox   | both | `instructions`    | rewrite only                  |
 * | Refinement chips                       | both | `instructions`    | rewrite (chip label)          |
 * | Persisted default instructions         | both | not `instructions`| via updateAiSettings / CET    |
 *
 * Empty-context Generate is allowed: occasion + tone alone must still produce
 * a request the API can fulfill. Suggest never sends session `instructions`.
 *
 * E-card-only request fields: `cardTemplateId`. Mailed-only: `maxBodyLength`.
 */

import {
  GreetingGenerationModes,
  type GenerateGreetingRequest,
} from '../constants/greetingGenerationModes';

/** Channel discriminator for the AI writing assistants. */
export type AiWritingMode =
  | typeof GreetingGenerationModes.ECard
  | typeof GreetingGenerationModes.MailedCard;

/** Suggest vs rewrite within a single assistant session. */
export type AiWritingRunMode = 'suggest' | 'rewrite';

/** Inputs for {@link buildAiWritingGenerationRequest}. */
export interface BuildAiWritingGenerationRequestArgs {
  writingMode: AiWritingMode;
  runMode: AiWritingRunMode;
  contactId: number;
  eventTypeId: number;
  /** Required for e-card; omitted for mailed. */
  cardTemplateId?: number;
  eventDate?: string;
  tone?: string;
  /**
   * Session rewrite-instructions or refinement chip label (rewrite only).
   * Empty string is treated as absent.
   */
  instructions?: string;
  /** Current body used as rewrite context when `runMode` is rewrite. */
  existingGreetingBody?: string;
  /** Mailed-card Cardly length cap. */
  maxBodyLength?: number;
}

/**
 * Maps UI session state into the API request shape without requiring intent.
 */
export function buildAiWritingGenerationRequest(
  args: BuildAiWritingGenerationRequestArgs,
): GenerateGreetingRequest {
  const {
    writingMode,
    runMode,
    contactId,
    eventTypeId,
    cardTemplateId,
    eventDate,
    tone,
    instructions,
    existingGreetingBody,
    maxBodyLength,
  } = args;

  const trimmedInstructions = instructions?.trim() ?? '';
  const trimmedBody = existingGreetingBody?.trim() ?? '';
  const trimmedEventDate = eventDate?.trim() ?? '';
  const trimmedTone = tone?.trim() ?? '';

  const request: GenerateGreetingRequest = {
    contactId,
    eventTypeId,
    mode: writingMode,
  };

  if (writingMode === GreetingGenerationModes.ECard && cardTemplateId != null) {
    request.cardTemplateId = cardTemplateId;
  }

  if (writingMode === GreetingGenerationModes.MailedCard && maxBodyLength != null) {
    request.maxBodyLength = maxBodyLength;
  }

  if (trimmedEventDate) {
    request.eventDate = trimmedEventDate;
  }

  if (trimmedTone) {
    request.tone = trimmedTone;
  }

  // Suggest: leave instructions unset so persisted CET AiGuidance alone shapes the draft.
  if (runMode === 'suggest') {
    return request;
  }

  // Rewrite: always send existing body when present; instructions are rewrite text / chips.
  if (trimmedBody) {
    request.existingGreetingBody = trimmedBody;
  }

  if (trimmedInstructions) {
    request.instructions = trimmedInstructions;
  }

  return request;
}
